import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { BiImport } from "react-icons/bi";
import { FaPlus } from "react-icons/fa";
import { FiEdit } from "react-icons/fi";
import { MdDeleteOutline } from "react-icons/md";
import { CiSearch } from "react-icons/ci";
import { FiFilter } from "react-icons/fi";

// --- Simple Calendar for in-line usage (no deps) ---
interface CalendarProps {
  // ISO string: YYYY-MM-DD, i.e. "2024-06-06"
  value?: string;
  onChange: (val: string) => void;
  highlightedDates?: string[]; // in YYYY-MM-DD format
}
const Calendar: React.FC<CalendarProps> = ({ value, onChange, highlightedDates = [] }) => {
  const today = new Date();
  const [current, setCurrent] = useState(() => {
    if (value) {
      const d = new Date(value);
      if (!isNaN(d.valueOf())) return new Date(d.getFullYear(), d.getMonth(), 1);
    }
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });

  const getDaysInMonth = (y: number, m: number) => new Date(y, m + 1, 0).getDate();
  const getWeekday = (y: number, m: number, d: number) => new Date(y, m, d).getDay();

  const selected = value ? new Date(value) : null;
  const year = current.getFullYear();
  const month = current.getMonth();
  const daysInMonth = getDaysInMonth(year, month);

  // prep highlight set for easy lookup
  const highlights = React.useMemo(
    () => new Set(highlightedDates),
    [highlightedDates]
  );

  // Start from which weekday
  const firstWeekDay = getWeekday(year, month, 1);

  // Generate grid
  let grid: (string | null)[] = [];
  for (let i = 0; i < firstWeekDay; i++) grid.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = `${year}-${(month+1).toString().padStart(2, '0')}-${d.toString().padStart(2, '0')}`
    grid.push(dateStr);
  }
  // Fill last row up to 7
  while (grid.length % 7 !== 0) grid.push(null);

  return (
    <div className="shadow border rounded bg-white p-3 z-10" style={{minWidth: 260}}>
      <div className="flex justify-between items-center mb-2">
        <button
          className="bg-transparent text-gray-500 px-2"
          onClick={() => setCurrent(new Date(year, month - 1, 1))}
          type="button"
        >‹</button>
        <span className="font-semibold text-sm">{current.toLocaleString('default', { month: 'long', year: 'numeric' })}</span>
        <button
          className="bg-transparent text-gray-500 px-2"
          onClick={() => setCurrent(new Date(year, month + 1, 1))}
          type="button"
        >›</button>
      </div>
      <div className="grid grid-cols-7 mb-1 text-xs text-center">
        {["Su","Mo","Tu","We","Th","Fr","Sa"].map(d => <div key={d} className="font-semibold">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 text-sm">
        {grid.map((dateStr, idx) => {
          if (dateStr === null) return <div key={idx} />;
          const d = new Date(dateStr);
          const displayDay = d.getDate();
          const isSelected = selected && dateStr === value;
          const highlight = highlights.has(dateStr);
          return (
            <button
              key={dateStr}
              type="button"
              className={`rounded-full w-8 h-8 mx-auto mb-1
                ${isSelected ? 'bg-[#1CAC70] text-white' : highlight ? 'bg-[#FFFAE5] text-black font-semibold border border-[#FF9500]' : 'bg-transparent hover:bg-[#F4F4F6]'}
                `}
              onClick={() => onChange(dateStr)}
            >
              {displayDay}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Helper to get unique values for filter dropdowns
const getUnique = (arr: any[], field: string): string[] => {
  if (field === 'categories') {
    // collect all category values, flatten
    return Array.from(
        new Set(arr.flatMap(item => Array.isArray(item.categories)
            ? item.categories.map((cat: string) => cat.trim())
            : []
        ).filter(Boolean))
      )
  }
  // else regular field
  return Array.from(
    new Set(
      arr.map(item => (item as any)[field])
      .filter(Boolean)
      .map((v: string) => v.toString().trim())
    )
  )
};

interface Party {
  id: number;
  date: string;
  name: string;
  number: string;
  contact_person_name: string;
  contact_person_number: string;
  categories: string[];
  total_amount?: number;
}

const TABLE_COLUMNS = [
  { key: "name", label: "Name" },
  { key: "date", label: "Date" },
  { key: "number", label: "Contact No." },
  { key: "contact_person_name", label: "Contact Person" },
  { key: "contact_person_number", label: "Contact Person's No." },
  { key: "categories", label: "Categories" },
];

function Addparty() {
  const [parties, setParties] = useState<Party[]>([])
  const [search, setSearch] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 10

  // Modal state
  const [showConfirm, setShowConfirm] = useState<boolean>(false)
  const [deleteId, setDeleteId] = useState<number | null>(null)

  const [showEdit, setShowEdit] = useState<boolean>(false)
  const [editParty, setEditParty] = useState<Party | null>(null)

  const [reload, setReload] = useState(0)

  // Error state for catching and showing errors
  const [error, setError] = useState<string | null>(null);

  // For Add Amount Modal
  const [showAddAmount, setShowAddAmount] = useState(false);
  const [selectedPartyId, setSelectedPartyId] = useState<number | null>(null);
  const [addAmount, setAddAmount] = useState("");
  const [isAddingAmount, setIsAddingAmount] = useState(false);
  const [addAmountError, setAddAmountError] = useState<string | null>(null);
  const [currentTotalAmount, setCurrentTotalAmount] = useState<number | null>(null);

  // Filters UI state: each column can have a filter
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [showFilters, setShowFilters] = useState<boolean>(false);

  // Track filter focus to display dropdown/calendar via col key
  const [activeFilterDropdown, setActiveFilterDropdown] = useState<string | null>(null);
  // For click outside to close filter dropdown/calendar
  const filterDropdownRefs = useRef<Record<string, HTMLDivElement|null>>({});

  // Fetch parties from API
  const fetchParties = useCallback(async () => {
    try {
      setError(null);
      const res = await fetch(`${process.env.REACT_APP_API_URL}/auth/addparty`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        setError(data?.message || `Failed to fetch: ${res.status}`);
        setParties([]);
        return;
      }
      const data = await res.json();
      if (Array.isArray(data)) {
        setParties(data.map((item: any) => ({
          ...item,
          categories: Array.isArray(item.categories)
            ? item.categories
            : typeof item.categories === "string"
              ? (item.categories[0] === "[" ? JSON.parse(item.categories) : item.categories.split(",").map((c: string) => c.trim()))
              : [],
          total_amount: item.total_amount !== undefined ? Number(item.total_amount) : undefined
        })));
      } else {
        setParties([]);
      }
    } catch (e: any) {
      setError(e?.message ? String(e.message) : "Error fetching parties");
      setParties([]);
    }
  }, []);

  useEffect(() => {
    fetchParties();
  }, [fetchParties, reload]);

  // Filter by main search first, then by column filters
  let filtered = parties.filter(p =>
    (p.name || "").toLowerCase().includes(search.toLowerCase())
  );

  // Apply individual filters
  filtered = filtered.filter((party) => {
    for (const col of TABLE_COLUMNS) {
      if (!filters[col.key]) continue; // filter not set
      const filterValue = filters[col.key].toLowerCase().trim();
      if (!filterValue) continue;
      if (col.key === 'categories') {
        // Check if any category matches
        if (!party.categories?.some(cat => cat.toLowerCase().includes(filterValue))) {
          return false;
        }
      } else if (col.key === 'date') {
        // Allow filter by string
        const dateStr = party.date
          ? new Date(party.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
          : '';
        // Can filter as string (e.g. May, 2024) or strictly YYYY-MM-DD
        const dateIso = party.date?.slice?.(0,10) || '';
        if (!dateStr.toLowerCase().includes(filterValue) && !dateIso.includes(filterValue)) return false;
      } else {
        if (!((party as any)[col.key] || '').toLowerCase().includes(filterValue)) {
          return false;
        }
      }
    }
    return true;
  });

  // Collect filter choices for dropdowns
  const filterDropdownOptions: Record<string, string[]> = {
    name: getUnique(parties, 'name'),
    number: getUnique(parties, 'number'),
    contact_person_name: getUnique(parties, 'contact_person_name'),
    contact_person_number: getUnique(parties, 'contact_person_number'),
    categories: getUnique(parties, 'categories'),
    date: getUnique(parties, 'date'),
  };

  // For date calendar: get all party dates in YYYY-MM-DD for highlight
  const partyDateStrings = getUnique(parties, 'date')
    .map(v => {
      // v might be ISO or a date string, ensure YYYY-MM-DD format
      try {
        const d = new Date(v);
        if (!isNaN(d.valueOf())) {
          return d.toISOString().split('T')[0];
        }
      } catch {}
      return null;
    })
    .filter(Boolean) as string[];

  // Close dropdowns/calendars if click outside
  useEffect(() => {
    const handleClick = (event: any) => {
      // Check if clicked inside any open dropdown/calendar
      let found = false;
      if (activeFilterDropdown && filterDropdownRefs.current[activeFilterDropdown]) {
        const ref = filterDropdownRefs.current[activeFilterDropdown];
        if (ref && ref.contains(event.target)) found = true;
      }
      if (!found) setActiveFilterDropdown(null);
    };
    if (activeFilterDropdown) {
      document.addEventListener('mousedown', handleClick);
      return () => document.removeEventListener('mousedown', handleClick);
    }
  }, [activeFilterDropdown]);

  // Pagination
  const totalPartyPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const partyPageData = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // Delete
  const deleteParty = async (id: number) => {
    try {
      setError(null);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/addparty`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id })
      });
      if (response.ok) {
        setReload(r => r + 1);
      } else {
        const data = await response.json().catch(() => ({}));
        setError(data.message || "Failed to delete party");
        alert(data.message || "Failed to delete party");
      }
    } catch (err: any) {
      setError(err?.message || 'Delete failed');
      alert('Delete failed');
    }
  };

  // Edit input handler
  const handleEditInput = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!editParty) return;
    const { name, value } = e.target;
    if (name === "categories") {
      setEditParty({
        ...editParty,
        categories: value.split(',').map(v => v.trim()).filter(Boolean)
      });
      return;
    }
    setEditParty({ ...editParty, [name]: value });
  };

  // Edit submit
  const handleUpdateParty = async () => {
    if (!editParty) return;
    setError(null);
    const payload = { ...editParty, categories: editParty.categories };
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/addparty`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (response.ok) {
        setShowEdit(false);
        setEditParty(null);
        setReload(r => r + 1);
      } else {
        const data = await response.json();
        setError(data.message || "Failed to update party");
        alert(data.message || "Failed to update party");
      }
    } catch (e: any) {
      setError(e?.message ? String(e.message) : "Failed to update");
      alert('Failed to update');
    }
  };

  // Add Amount: open modal, set party
  const openAddAmountModal = (partyId: number) => {
    setAddAmountError(null);
    setAddAmount('');
    setSelectedPartyId(partyId);
    setShowAddAmount(true);
    setCurrentTotalAmount(null);
    // Fetch party latest total_amount
    const party = parties.find(p => p.id === partyId);
    console.log("party data ", party);
    if (party && typeof party.total_amount === "number") {
      setCurrentTotalAmount(Number(party.total_amount));
    } else {
      // fallback to fetching again (API), or keep as null if not found
    }
  };

  // When modal open and party selected, always refresh latest total amount
  useEffect(() => {
    // Don't fetch if closed or no party
    if (!showAddAmount || !selectedPartyId) return;
    // Always fetch latest from API for correct value
    const fetchPartyAmount = async () => {
      setCurrentTotalAmount(null);
      const party = parties.find(p => Number(p.id) === Number(selectedPartyId));
      if (party && party.total_amount != null && !isNaN(Number(party.total_amount))) {
        setCurrentTotalAmount(Number(party.total_amount));
      }

    };
    fetchPartyAmount();
  }, [showAddAmount, selectedPartyId]);

  // Add Amount: handle submit
  const handleAddAmount = async () => {
    setAddAmountError(null);
    if (!selectedPartyId) return;
    if (!addAmount || isNaN(Number(addAmount)) || Number(addAmount) <= 0) {
      setAddAmountError("Please enter a valid amount greater than 0.");
      return;
    }

    setIsAddingAmount(true);
    try {
      // Find the original party data by ID
      const oldParty = parties.find(p => Number(p.id) === Number(selectedPartyId));
      if (!oldParty) {
        setAddAmountError("Party not found.");
        setIsAddingAmount(false);
        return;
      }

      // Calculate new total_amount
      let baseAmount = 0;
      if (oldParty.total_amount != null && !isNaN(Number(oldParty.total_amount))) {
        baseAmount = Number(oldParty.total_amount);
      }
      const newTotalAmount = baseAmount + Number(addAmount);

      // Prepare the full data payload, only updating total_amount
      // Convert categories to the format the API expects (as stringified JSON array)
      const formattedCategories = Array.isArray(oldParty.categories)
        ? JSON.stringify(oldParty.categories)
        : typeof oldParty.categories === 'string'
          ? oldParty.categories
          : "[]";

      const dataToSend = {
        id: oldParty.id,
        name: oldParty.name,
        number: oldParty.number,
        contact_person_name: oldParty.contact_person_name,
        contact_person_number: oldParty.contact_person_number,
        categories: formattedCategories,
        date: oldParty.date ? new Date(oldParty.date).toISOString().slice(0, 10) : "",
        total_amount: newTotalAmount,
      };

      // Only total_amount is being changed; rest are same as before
      const res = await fetch(`${process.env.REACT_APP_API_URL}/auth/addparty`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dataToSend)
      });

      if (res.ok) {
        setShowAddAmount(false);
        setAddAmount('');
        setSelectedPartyId(null);
        setCurrentTotalAmount(null);
        setReload(r => r + 1);
      } else {
        const errorData = await res.json().catch(() => ({}));
        setAddAmountError(errorData.message || "Failed to add amount");
      }
    } catch (e: any) {
      setAddAmountError(e?.message ?? "Failed to add amount");
    }
    setIsAddingAmount(false);
  };

  // UI
  return (
    <div className='p-6'>

      {/* Error display */}
      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-800 rounded">
          {error}
        </div>
      )}

      {/* HEADER */}
      <div className='flex flex-wrap justify-between w-full'>
        <div className='flex items-center gap-4'>
          <h1 className='text-[28px] font-[500]'>Parties</h1>
          <div className='bg-[#F9F5FF] p-2 rounded-[40px]'>
            <span className='text-[#6941C6] text-[12px]'>{filtered.length} vendors</span>
          </div>
        </div>

        <div className='flex items-center gap-4'>
          <button className='flex items-center gap-1 border-[1px] border-[#D0D5DD] bg-[#fff] py-2 px-4 rounded-[8px]'>
            <BiImport /> Import
          </button>

          <Link to='/addnewparty'>
            <button className="flex items-center gap-2 text-[16px] font-[500] text-[#fff] bg-gradient-to-r from-[#1CAC70] to-[#FF9500] py-2 rounded-[8px] px-4">
              <FaPlus /> Add Party
            </button>
          </Link>
          {/* ADD AMOUNT BUTTON */}
          <button
            className='flex items-center gap-2 text-[16px] font-[500] text-white bg-gradient-to-r from-[#FF9500] to-[#1CAC70] py-2 rounded-[8px] px-4'
            onClick={() => setShowAddAmount(true)}
          >
            + Add Amount
          </button>
        </div>
      </div>

      <span className='text-[#667085] text-[14px]'>Keep track of Parties.</span>

      {/* SEARCH and FILTER BUTTON */}
      <div className='flex justify-between w-[100%] mt-7'>
        <div className="w-[40%] bg-[#fff] h-[38px] flex items-center rounded-[8px] px-2 border-[#D0D5DD] border-[1px] gap-1">
          <CiSearch className="text-[#8488B7] text-[24px]" />
          <input
            type="text"
            placeholder="Search..."
            className="w-full bg-transparent h-[32px] focus:outline-none"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1); // reset to first page on search
            }}
          />
        </div>
        {/* FILTER BUTTON */}
        <button
          className="flex items-center gap-1 border border-[#D0D5DD] bg-white py-2 px-4 rounded-[8px] text-[#6941C6] hover:bg-[#F4F3FF] transition"
          onClick={() => setShowFilters(v => !v)}
        >
          <FiFilter /> Filter
        </button>
      </div>

      {/* FILTERS ROW */}
      {showFilters && (
        <div className="mt-3 mb-2 px-2 py-3 rounded-[8px] border border-[#E0E0E0] bg-[#F9FAFB] flex flex-wrap gap-x-6 gap-y-2 relative">
          {TABLE_COLUMNS.map(col => {
            const isDropdown =
              col.key === 'name'
              || col.key === 'number'
              || col.key === 'contact_person_name'
              || col.key === 'contact_person_number'
              || col.key === 'categories';

            const isDate = col.key === 'date';

            // Attach ref for outside click
            const setRef = (el: HTMLDivElement | null) => {
              filterDropdownRefs.current[col.key] = el;
            };

            return (
              <div key={col.key} className="flex flex-col justify-start relative" ref={isDropdown || isDate ? setRef : undefined}>
                <label htmlFor={`filter-${col.key}`} className="text-xs text-[#5C5C73] font-medium mb-1">{col.label}</label>
                {!isDate ? (
                  <div className="relative">
                    <input
                      id={`filter-${col.key}`}
                      type="text"
                      autoComplete="off"
                      className="border border-[#D0D5DD] rounded-[5px] px-2 py-1 text-sm bg-white min-w-[140px]"
                      placeholder={`Filter ${col.label}`}
                      value={filters[col.key] || ''}
                      onChange={e => {
                        setFilters(f => ({
                          ...f,
                          [col.key]: e.target.value,
                        }));
                        setCurrentPage(1);
                      }}
                      onFocus={() => isDropdown && setActiveFilterDropdown(col.key)}
                      onClick={e => isDropdown && setActiveFilterDropdown(col.key)}
                      readOnly={isDropdown}
                    />
                    {/* Dropdown for filters */}
                    {isDropdown && activeFilterDropdown === col.key && (
                      <div
                        className="absolute z-20 bg-white shadow rounded mt-1 border border-gray-200 max-h-40 overflow-y-auto left-0 w-full"
                        tabIndex={0}
                        style={{ minWidth: 150, maxWidth: 300 }}
                      >
                        {filterDropdownOptions[col.key].length === 0 ? (
                          <div className="px-3 py-2 text-xs text-gray-400">
                            No options
                          </div>
                        ) : (
                          filterDropdownOptions[col.key].map(option => (
                            <div
                              key={option}
                              className="px-3 py-2 hover:bg-[#F7F7FB] cursor-pointer text-sm"
                              style={{ whiteSpace: 'nowrap' }}
                              onClick={() => {
                                setFilters(f => ({
                                  ...f,
                                  [col.key]: option
                                }));
                                setCurrentPage(1);
                                setActiveFilterDropdown(null);
                              }}
                            >
                              {option}
                            </div>
                          ))
                        )}
                        {col.key === 'categories' && filterDropdownOptions[col.key].length > 0 &&
                          <div
                            className="px-3 py-2 text-xs text-[#BDBDBD] border-t mt-1 cursor-pointer"
                            onClick={() => {
                              setFilters(f => ({
                                ...f,
                                [col.key]: ''
                              }));
                              setActiveFilterDropdown(null);
                            }}
                          >
                            Clear selection
                          </div>
                        }
                      </div>
                    )}
                  </div>
                ) : (
                  // Date: show fake input to trigger calendar, and calendar popup
                  <div className="relative">
                    <input
                      id={`filter-${col.key}`}
                      type="text"
                      className="border border-[#D0D5DD] rounded-[5px] px-2 py-1 text-sm bg-white min-w-[140px] cursor-pointer"
                      placeholder={`Filter ${col.label}`}
                      value={filters[col.key] || ''}
                      onClick={() => setActiveFilterDropdown(col.key)}
                      onFocus={() => setActiveFilterDropdown(col.key)}
                      readOnly
                    />
                    {/* Calendar popup */}
                    {activeFilterDropdown === col.key && (
                      <div className="absolute z-20 left-0 mt-2">
                        <Calendar
                          value={filters.date}
                          onChange={val => {
                            setFilters(f => ({
                              ...f,
                              [col.key]: val,
                            }));
                            setActiveFilterDropdown(null);
                            setCurrentPage(1);
                          }}
                          highlightedDates={partyDateStrings}
                        />
                        <div
                          className="mt-2 text-xs text-right text-blue-400 cursor-pointer underline"
                          onClick={() => {
                            setFilters(f => ({ ...f, [col.key]: '' }));
                            setActiveFilterDropdown(null);
                          }}
                        >
                          Clear date filter
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          <button
            className="ml-auto mt-6 px-4 py-1 rounded text-xs bg-[#FAD1B1] text-[#E86D0C] font-medium hover:bg-[#FFEEDB] border border-[#F9BB6A] transition"
            onClick={() => setFilters({})}
            type="button"
          >
            Clear Filters
          </button>
        </div>
      )}

      {/* TABLE */}
      <div className="max-w-full overflow-x-auto border border-gray-200 mt-4">
        <table className="w-full border-separate border-spacing-0 bg-white rounded-xl shadow-sm">
          <thead>
            <tr className="bg-[#F9FAFB] border-b border-[#EFEFEF]">
              <th className="py-3 px-5 text-left text-xs font-semibold text-[#667085] rounded-tl-xl">
                Name
              </th>
              <th className="py-3 px-5 text-left text-xs font-semibold text-[#667085]">
                Date
              </th>
              <th className="py-3 px-5 text-left text-xs font-semibold text-[#667085]">
                Contact No.
              </th>
              <th className="py-3 px-5 text-left text-xs font-semibold text-[#667085]">
                Contact Person
              </th>
                <th className="py-3 px-5 text-left text-xs font-semibold text-[#667085]">
                Contact Person's number
              </th>
              <th className="py-3 px-5 text-left text-xs font-semibold text-[#667085]">
                Categories
              </th>
              <th className="py-3 px-4 rounded-tr-xl"></th>
            </tr>
          </thead>
          <tbody>
            {partyPageData.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center py-8 text-[#BDBDBD] text-sm font-medium border-b border-[#F4F4F4] bg-white">
                  No Parties Found
                </td>
              </tr>
            ) : (
              partyPageData.map((party) => (
                <tr
                  key={party.id}
                  className="border-b border-[#F4F4F4] hover:bg-[#F7F7FB] group transition"
                >
                  {/* Party avatar + name */}
                  <td className="flex items-center gap-3 py-3 px-5 text-sm text-[#232323] bg-white font-semibold rounded-l-xl">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full ring-2 ring-[#C9E7FF] bg-gradient-to-tr from-[#E3F1FF] to-[#F3F8FE] mr-2">
                      {/* Example icon/circle */}
                      <img
                        src="/avatar-party.svg"
                        alt="Party"
                        className="w-7 h-7 rounded-full"
                        onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                      />
                      {/* If no avatar, show fallback icon/circle */}
                    </div>
                    <span className="text-[#232323] text-sm font-semibold">
                      {party.name || <span className="italic text-[#BDBDBD]">Unnamed</span>}
                    </span>
                  </td>
                  <td className="py-3 px-5 whitespace-nowrap bg-white text-[#828282] text-[13px] font-normal">
                    {party.date ? new Date(party.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '--'}
                  </td>
                  <td className="py-3 px-5 whitespace-nowrap bg-white text-[#828282] text-[13px] font-normal">
                    {party.number || '--'}
                  </td>
                  <td className="py-3 px-5 whitespace-nowrap bg-white text-[#4F4F4F] text-[13px] font-normal">
                    <span>{party.contact_person_name || '--'}</span>
                  </td>
                   <td className="py-3 px-5 whitespace-nowrap bg-white text-[#4F4F4F] text-[13px] font-normal">
                    <span>{party.contact_person_number || '--'}</span>
                  </td>
                  <td className="py-3 px-5 bg-white">
                    {Array.isArray(party.categories) && party.categories.length > 0 ? (
                      party.categories.map((category: string, index: number) => {
                        let colorClass = "bg-[#E9F9EE] text-[#53B176]";
                        if (category.toUpperCase() === "FNO") colorClass = "bg-[#E9F0FB] text-[#2156CF]";
                        if (category.toUpperCase() === "MF") colorClass = "bg-[#EAFDFB] text-[#0D9488]";
                        if (category.toUpperCase() === "EQUITY") colorClass = "bg-[#E9F9EE] text-[#53B176]";
                        return (
                          <span
                            key={category + index}
                            className={`inline-block px-3 py-0.5 text-[12px] font-medium rounded-xl mr-2 mb-1 border border-[#E0E1E7] ${colorClass}`}
                          >
                            {category}
                          </span>
                        );
                      })
                    ) : (
                      <span className="text-[12px] text-[#BDBDBD] font-normal">No Categories</span>
                    )}
                  </td>
                  <td className="py-3 px-4 bg-white flex items-center gap-3 rounded-r-xl">
                    {/* ADD AMOUNT BUTTON (inline) */}
                    <button
                      className="p-2 hover:bg-[#FFFAE5] rounded-full text-[#FFA500] hover:text-[#E86D0C] transition"
                      title="Add Amount"
                      onClick={() => openAddAmountModal(party.id)}
                    >
                      +
                    </button>
                    {/* DELETE */}
                    <button
                      className="p-2 hover:bg-[#F5F5F7] rounded-full text-[#BDBDBD] hover:text-[#EA5050] transition"
                      title="Delete"
                      onClick={() => {
                        setDeleteId(party.id)
                        setShowConfirm(true)
                      }}
                    >
                      <MdDeleteOutline size={18} />
                    </button>
                    {/* EDIT */}
                    <button
                      className="p-2 hover:bg-[#F5F5F7] rounded-full text-[#BDBDBD] hover:text-[#6941C6] transition"
                      title="Edit"
                      onClick={() => {
                        setEditParty(party)
                        setShowEdit(true)
                      }}
                    >
                      <FiEdit size={16} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ADD AMOUNT MODAL */}
      {showAddAmount && (
        <div className="fixed inset-0 bg-black/40 flex justify-center items-center z-50">
          <div className="bg-white shadow-lg rounded-2xl w-full max-w-md p-7 relative">
            <button
              className="absolute top-2 right-3 text-gray-400 text-2xl"
              onClick={() => {
                setShowAddAmount(false);
                setSelectedPartyId(null);
                setAddAmount('');
                setCurrentTotalAmount(null);
                setAddAmountError(null);
              }}
            >
              &times;
            </button>
            <h2 className='text-xl font-bold mb-4 text-gray-800'>Add Amount to Party</h2>

            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Party</label>
              <select
                className="w-full border rounded px-3 py-2"
                value={selectedPartyId || ""}
                onChange={e => {
                  setSelectedPartyId(Number(e.target.value));
                  setAddAmount('');
                  setCurrentTotalAmount(null);
                  setAddAmountError(null);
                }}
                disabled={isAddingAmount}
              >
                <option value="" disabled>Select party</option>
                {parties.map(p =>
                  <option key={p.id} value={p.id}>{p.name}</option>
                )}
              </select>
            </div>

            {selectedPartyId && (
              <div className="mb-3 hidden">
                <label className="block text-sm mb-1 text-gray-700">Current Total Amount</label>
                <div className="text-gray-900 text-base font-semibold pl-1">
                  {currentTotalAmount !== null ? `₹${currentTotalAmount}` : "Loading..."}
                </div>
              </div>
            )}

            {selectedPartyId && (
              <div className="mb-3">
                <label className="block text-sm font-medium mb-1">Amount to Add</label>
                <input
                  type="number"
                  min={0}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  value={addAmount}
                  disabled={isAddingAmount}
                  onChange={e => setAddAmount(e.target.value)}
                  placeholder="Enter amount"
                />
              </div>
            )}

            {addAmountError && (
              <div className="text-red-500 text-sm mb-3">{addAmountError}</div>
            )}

            <div className="mt-6 flex justify-end gap-3">
              <button
                className="px-5 py-2 rounded bg-gray-200 text-gray-700"
                onClick={() => {
                  setShowAddAmount(false);
                  setSelectedPartyId(null);
                  setAddAmount('');
                  setCurrentTotalAmount(null);
                  setAddAmountError(null);
                }}
                disabled={isAddingAmount}
              >
                Cancel
              </button>
              <button
                className="px-5 py-2 rounded text-white bg-gradient-to-r from-[#FF9500] to-[#1CAC70] font-semibold"
                onClick={handleAddAmount}
                disabled={isAddingAmount || !selectedPartyId || !addAmount}
              >
                {isAddingAmount ? "Adding..." : "Add Amount"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DELETE CONFIRM MODAL */}
      {showConfirm && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
          <div className="bg-white rounded-2xl shadow-lg p-6 w-80 text-center">
            <h2 className="text-lg font-semibold text-gray-800 mb-2">Delete Party?</h2>
            <p className="text-sm text-gray-500 mb-4">
              Are you sure you want to delete this Party?
            </p>
            <div className="flex justify-center space-x-3">
              <button
                onClick={() => {
                  setShowConfirm(false)
                  setDeleteId(null)
                }}
                className="px-4 py-2 rounded-lg bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (deleteId !== null) await deleteParty(deleteId)
                  setShowConfirm(false)
                  setDeleteId(null)
                }}
                className="px-4 py-2 rounded-lg text-white bg-gradient-to-r from-[#1CAC70] to-[#FF9500]"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* EDIT MODAL */}
      {showEdit && editParty && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
          <div className="bg-[#ffff] p-5 w-[600px] rounded-[10px]">

            <div className='w-full flex justify-center'>
              <h1 className='text-[28px] text-[#101828] font-[500]'>Edit Party</h1>
            </div>

            {/* All fields except id */}
            <div className='grid grid-cols-2 gap-4 mt-4'>
              <div className='col-span-1'>
                <label htmlFor="id">Id</label>
                <input
                  type="number"
                  id="id"
                  name="id"
                  value={editParty.id}
                  readOnly
                  className='w-full border p-2 rounded-[8px] mt-1 bg-gray-100 cursor-not-allowed'
                />
              </div>
              <div className='col-span-1'>
                <label htmlFor="date">Date</label>
                <input
                  type="date"
                  id="date"
                  name="date"
                  value={editParty.date ? new Date(editParty.date).toISOString().split('T')[0] : ''}
                  onChange={handleEditInput}
                  className='w-full border p-2 rounded-[8px] mt-1'
                />
              </div>
              <div className='col-span-1'>
                <label htmlFor="name">Party Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={editParty.name}
                  onChange={handleEditInput}
                  className='w-full border p-2 rounded-[8px] mt-1'
                />
              </div>
              <div className='col-span-1'>
                <label htmlFor="number">Contact Number</label>
                <input
                  type="text"
                  id="number"
                  name="number"
                  value={editParty.number}
                  onChange={handleEditInput}
                  className='w-full border p-2 rounded-[8px] mt-1'
                />
              </div>
              <div className='col-span-1'>
                <label htmlFor="contact_person_name">Contact Person Name</label>
                <input
                  type="text"
                  id="contact_person_name"
                  name="contact_person_name"
                  value={editParty.contact_person_name}
                  onChange={handleEditInput}
                  className='w-full border p-2 rounded-[8px] mt-1'
                />
              </div>
              <div className='col-span-1'>
                <label htmlFor="contact_person_number">Contact Person Number</label>
                <input
                  type="text"
                  id="contact_person_number"
                  name="contact_person_number"
                  value={editParty.contact_person_number}
                  onChange={handleEditInput}
                  className='w-full border p-2 rounded-[8px] mt-1'
                />
              </div>
              <div className='col-span-2'>
                <label htmlFor="categories">Categories <span className='text-xs text-gray-400'>(Comma separated)</span></label>
                <input
                  type="text"
                  id="categories"
                  name="categories"
                  value={Array.isArray(editParty.categories) ? editParty.categories.join(', ') : ''}
                  onChange={handleEditInput}
                  className='w-full border p-2 rounded-[8px] mt-1'
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowEdit(false)}
                className="px-4 py-2 rounded-lg bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateParty}
                className="px-4 py-2 rounded-lg text-white bg-gradient-to-r from-[#1CAC70] to-[#FF9500]"
              >
                Submit
              </button>
            </div>

          </div>
        </div>
      )}

      {/* PAGINATION */}
      <div className="flex items-center justify-between mt-4 space-x-3 relative z-0">
        <span className="text-[#344054] text-[14px]">
          Page {currentPage} of {totalPartyPages}
        </span>

        <div className='flex gap-2'>
          <button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(prev => prev - 1)}
            className="px-3 py-1 bg-[#fff] disabled:opacity-50 border rounded-[6px]"
          >
            Previous
          </button>

          <button
            disabled={currentPage === totalPartyPages}
            onClick={() => setCurrentPage(prev => prev + 1)}
            className="px-3 py-1 bg-[#fff] disabled:opacity-50 border rounded-[6px]"
          >
            Next
          </button>
        </div>
      </div>

    </div>
  )
}

export default Addparty
